import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { PERMISSIONS } from '../constants/permissions';
import sideBarIcon from '../assets/icons/side_bar_ic.png';
import { ReactComponent as SelectedOverviewIcon } from '../assets/svg_icons/selected_overview.svg';
import { ReactComponent as SelectedProjectsIcon } from '../assets/svg_icons/selected_projects.svg';
import { ReactComponent as SelectedProtocolsIcon } from '../assets/svg_icons/selected_protocols.svg';
import { ReactComponent as SelectedTicketIcon } from '../assets/svg_icons/selected_ticket.svg';
import { ReactComponent as SelectedFinanceIcon } from '../assets/svg_icons/selected_finance.svg';
import { ReactComponent as SelectedCustomersIcon } from '../assets/svg_icons/selected_costumers.svg';
import { ReactComponent as SelectedUpdatesIcon } from '../assets/svg_icons/selected_updates.svg';
import { ReactComponent as SelectedLogsIcon } from '../assets/svg_icons/selected_logs.svg';
import { ReactComponent as UnSelectedOverviewIcon } from '../assets/svg_icons/unselected_overview.svg';
import { ReactComponent as UnSelectedProjectsIcon } from '../assets/svg_icons/unselected_projects.svg';
import { ReactComponent as UnSelectedProtocolsIcon } from '../assets/svg_icons/unselected_protocols.svg';
import { ReactComponent as UnSelectedTicketIcon } from '../assets/svg_icons/unselected_ticket.svg';
import { ReactComponent as UnSelectedFinanceIcon } from '../assets/svg_icons/unselected_finance.svg';
import { ReactComponent as UnSelectedCustomersIcon } from '../assets/svg_icons/unselected_costumers.svg';
import { ReactComponent as UnSelectedUpdatesIcon } from '../assets/svg_icons/unselected_updates.svg';
import { ReactComponent as UnSelectedLogsIcon } from '../assets/svg_icons/unselected_logs.svg';

import './Sidebar.css';

const getTabIcon = (tabName, isSelected) => {
    switch (tabName) {
        case 'overview':
            return isSelected ? <SelectedOverviewIcon width={20} height={20} /> : <UnSelectedOverviewIcon width={20} height={20} />;
        case 'projects':
            return isSelected ? <SelectedProjectsIcon width={20} height={20} /> : <UnSelectedProjectsIcon width={20} height={20} />;
        case 'protocols':
            return isSelected ? <SelectedProtocolsIcon width={20} height={20} /> : <UnSelectedProtocolsIcon width={20} height={20} />;
        case 'tickets':
            return isSelected ? <SelectedTicketIcon width={20} height={20} /> : <UnSelectedTicketIcon width={20} height={20} />;
        case 'finance':
            return isSelected ? <SelectedFinanceIcon width={20} height={20} /> : <UnSelectedFinanceIcon width={20} height={20} />;
        case 'maintenance':
            return isSelected ? <SelectedFinanceIcon width={20} height={20} /> : <UnSelectedFinanceIcon width={20} height={20} />;
        case 'utility':
            return isSelected ? <SelectedFinanceIcon width={20} height={20} /> : <UnSelectedFinanceIcon width={20} height={20} />;
        case 'customers':
            return isSelected ? <SelectedCustomersIcon width={20} height={20} /> : <UnSelectedCustomersIcon width={20} height={20} />;
        case 'customer-profile':
            return isSelected ? <SelectedCustomersIcon width={20} height={20} /> : <UnSelectedCustomersIcon width={20} height={20} />;
        case 'groups':
            return isSelected ? <SelectedCustomersIcon width={20} height={20} /> : <UnSelectedCustomersIcon width={20} height={20} />;
        case 'myclub':
            return isSelected ? <SelectedCustomersIcon width={20} height={20} /> : <UnSelectedCustomersIcon width={20} height={20} />;
        case 'updates':
            return isSelected ? <SelectedUpdatesIcon width={20} height={20} /> : <UnSelectedUpdatesIcon width={20} height={20} />;
        case 'logs':
            return isSelected ? <SelectedLogsIcon width={20} height={20} /> : <UnSelectedLogsIcon width={20} height={20} />;
        case 'settings':
            return isSelected ? '⚙️' : '⚙️';
        default:
            return null;
    }
};

const Sidebar = memo(({ selectedTab, onTabSelect, isOpen, onClose }) => {
    const navigate = useNavigate();

    // Use cached permissions to avoid repeated checks on tab clicks
    const { hasShowInsight, hasShowProject, hasShowOffers, hasShowTicket, hasShowCategory, hasShowFinance, hasShowCustomer, hasShowClub, hasShowSwagUpdates, hasShowAdminLogs, hasShowUserLogs, permissionsLoading } = useCachedPermissions();

    const [expandedStates, setExpandedStates] = useState({
        finance: false,
        'finance-paid': false,
        'finance-unpaid': false,
        maintenance: false,
        'maintenance-paid': false,
        'maintenance-unpaid': false,
        utility: false,
        'utility-paid': false,
        'utility-unpaid': false,
        tickets: false,
        'tickets-requests': false,
        'tickets-closed': false,
        'tickets-categories': false,
        updates: false,
        'updates-units': false,
        'updates-swag': false,
        logs: false,
        'logs-admin': false,
        'logs-user': false
    });

    React.useEffect(() => {
        const newExpandedStates = {
            finance: false,
            'finance-paid': false,
            'finance-unpaid': false,
            maintenance: false,
            'maintenance-paid': false,
            'maintenance-unpaid': false,
            utility: false,
            'utility-paid': false,
            'utility-unpaid': false,
            tickets: false,
            'tickets-requests': false,
            'tickets-closed': false,
            'tickets-categories': false,
            updates: false,
            'updates-units': false,
            'updates-swag': false,
            logs: false,
            'logs-admin': false,
            'logs-user': false
        };

        // Set expanded states based on current selected tab
        if (selectedTab === 'finance' || selectedTab.startsWith('finance-')) {
            newExpandedStates.finance = true;
        }

        if (selectedTab === 'maintenance' || selectedTab.startsWith('maintenance-')) {
            newExpandedStates.maintenance = true;
        }

        if (selectedTab === 'utility' || selectedTab.startsWith('utility-')) {
            newExpandedStates.utility = true;
        }

        if (selectedTab === 'logs' || selectedTab.startsWith('logs-')) {
            newExpandedStates.logs = true;
        }

        if (selectedTab === 'tickets' || selectedTab.startsWith('tickets-')) {
            newExpandedStates.tickets = true;
        }

        if (selectedTab === 'updates' || selectedTab.startsWith('updates-')) {
            newExpandedStates.updates = true;
        }

        setExpandedStates(newExpandedStates);
    }, [selectedTab]);

    // Don't render sidebar until permissions are loaded to prevent flash
    if (permissionsLoading) {
        return (
            <div className={`sidebar ${isOpen ? 'open' : ''}`}>
                <div className="sidebar-icon">
                    <img src={sideBarIcon} alt="Sidebar Icon" />
                </div>
                <nav className="sidebar-nav">
                    <ul className="nav-list">
                        {/* Show all tabs during loading to prevent layout shift */}
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedOverviewIcon width={20} height={20} /></span>
                                <span className="nav-label">Overview</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedProjectsIcon width={20} height={20} /></span>
                                <span className="nav-label">Projects</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedProtocolsIcon width={20} height={20} /></span>
                                <span className="nav-label">Offers</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedTicketIcon width={20} height={20} /></span>
                                <span className="nav-label">Tickets</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedFinanceIcon width={20} height={20} /></span>
                                <span className="nav-label">Finance</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedCustomersIcon width={20} height={20} /></span>
                                <span className="nav-label">Customers</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedCustomersIcon width={20} height={20} /></span>
                                <span className="nav-label">My Club</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedUpdatesIcon width={20} height={20} /></span>
                                <span className="nav-label">Swag Updates</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon"><UnSelectedLogsIcon width={20} height={20} /></span>
                                <span className="nav-label">Logs</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className="nav-button">
                                <span className="nav-icon">⚙️</span>
                                <span className="nav-label">Settings</span>
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    }

    const handleTabClick = (tabName) => {
        if (tabName === 'finance') {
            setExpandedStates(prev => ({
                ...prev,
                finance: !prev.finance
            }));
        } else if (tabName === 'maintenance') {
            setExpandedStates(prev => ({
                ...prev,
                maintenance: !prev.maintenance
            }));
        } else if (tabName === 'utility') {
            setExpandedStates(prev => ({
                ...prev,
                utility: !prev.utility
            }));
        } else if (tabName === 'tickets') {
            setExpandedStates(prev => ({
                ...prev,
                tickets: !prev.tickets
            }));
        }
        else if (tabName === 'logs') {
            setExpandedStates(prev => ({
                ...prev,
                logs: !prev.logs
            }));
        }
        else {
            // Collapse all expanded states when clicking on other tabs
            setExpandedStates({
                finance: false,
                'finance-paid': false,
                'finance-unpaid': false,
                maintenance: false,
                'maintenance-paid': false,
                'maintenance-unpaid': false,
                utility: false,
                'utility-paid': false,
                'utility-unpaid': false,
                tickets: false,
                'tickets-requests': false,
                'tickets-closed': false,
                'tickets-categories': false,
                updates: false,
                'updates-units': false,
                'updates-swag': false,
                logs: false,
                'logs-admin': false,
                'logs-user': false
            });
            // Navigate to the tab route
            navigate(`/${tabName}`);
        }
    };

    const isTabSelected = (tabName) => {
        if (tabName === 'projects') {
            return selectedTab === 'projects';
        }
        if (tabName === 'tickets') {
            return selectedTab === 'tickets' || selectedTab.startsWith('tickets-');
        }
        if (tabName === 'finance') {
            return selectedTab === 'finance' || selectedTab.startsWith('finance-');
        }
        if (tabName === 'maintenance') {
            return selectedTab === 'maintenance' || selectedTab.startsWith('maintenance-');
        }
        if (tabName === 'utility') {
            return selectedTab === 'utility' || selectedTab.startsWith('utility-');
        }
        if (tabName === 'customers') {
            return selectedTab === 'customers' || selectedTab === 'customer-profile' || selectedTab === 'groups';
        }
        // if (tabName === 'updates') {
        //     return selectedTab === 'updates' || selectedTab.startsWith('updates-');
        // }
        return selectedTab === tabName;
    };

    const isFinanceSubTabSelected = (subTab) => {
        return selectedTab === `finance-${subTab}`;
    };

    const isMaintenanceSubTabSelected = (subTab) => {
        return selectedTab === `maintenance-${subTab}`;
    };

    const isUtilitySubTabSelected = (subTab) => {
        return selectedTab === `utility-${subTab}`;
    };

    const isTicketsSubTabSelected = (subTab) => {
        return selectedTab === `tickets-${subTab}`;
    };

    const isLogsSubTabSelected = (subTab) => {
        return selectedTab === `logs-${subTab}`;
    };

    const handleFinanceSubTabClick = (subTab) => {
        navigate(`/finance/${subTab}`);
    };

    const handleMaintenanceSubTabClick = (subTab) => {
        navigate(`/maintenance/${subTab}`);
    };

    const handleUtilitySubTabClick = (subTab) => {
        navigate(`/utility/${subTab}`);
    };

    const handleTicketsSubTabClick = (subTab) => {
        navigate(`/tickets/${subTab}`);
    };

    const handleLogsSubTabClick = (subTab) => {
        navigate(`/logs/${subTab}`);
    };

    return (
        <div className={`sidebar ${isOpen ? 'open' : ''}`}>
            <div className="sidebar-icon">
                <img src={sideBarIcon} alt="Sidebar Icon" />
            </div>

            {/* Navigation Tabs */}
            <nav className="sidebar-nav">
                <ul className="nav-list">
                    {[
                        hasShowInsight && { name: 'overview', label: 'Overview', permission: PERMISSIONS.SHOW_INSIGHT },
                        hasShowProject && { name: 'projects', label: 'Projects', permission: PERMISSIONS.SHOW_PROJECT },
                        hasShowOffers && { name: 'protocols', label: 'Offers' },
                        hasShowTicket && { name: 'tickets', label: 'Tickets' },
                        hasShowFinance && { name: 'finance', label: 'Finance' },
                        hasShowFinance && { name: 'maintenance', label: 'Maintenance' },
                        hasShowFinance && { name: 'utility', label: 'Utility' },
                        hasShowCustomer && { name: 'customers', label: 'Customers' },
                        hasShowClub && { name: 'myclub', label: 'My Club' },
                        hasShowSwagUpdates && { name: 'updates', label: 'Swag Updates' },
                        (hasShowAdminLogs || hasShowUserLogs) && { name: 'logs', label: 'Logs' },
                        { name: 'settings', label: 'Settings' },
                    ]
                        .filter(Boolean) // Remove false/null values when hasShowInsight is false
                        .map((tab) => (
                            <li key={tab.name} className="nav-item">
                                <button
                                    className={`nav-button ${isTabSelected(tab.name) ? 'selected' : ''}`}
                                    onClick={() => handleTabClick(tab.name)}
                                >
                                    <span className="nav-icon">{getTabIcon(tab.name, isTabSelected(tab.name))}</span>
                                    <span className="nav-label">{tab.label}</span>
                                </button>

                                {/* Finance Sub-tabs */}
                                {tab.name === 'finance' && expandedStates.finance && (
                                    <ul className="sub-nav-list">
                                        {['paid', 'unpaid'].map((subTab) => (
                                            <li key={subTab} className="sub-nav-item">
                                                <button
                                                    className={`sub-nav-button ${isFinanceSubTabSelected(subTab) ? 'selected' : ''}`}
                                                    onClick={() => handleFinanceSubTabClick(subTab)}
                                                >
                                                    <span className="sub-nav-label">{subTab.charAt(0).toUpperCase() + subTab.slice(1)}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* Maintenance Sub-tabs */}
                                {tab.name === 'maintenance' && expandedStates.maintenance && (
                                    <ul className="sub-nav-list">
                                        {['paid', 'unpaid'].map((subTab) => (
                                            <li key={subTab} className="sub-nav-item">
                                                <button
                                                    className={`sub-nav-button ${isMaintenanceSubTabSelected(subTab) ? 'selected' : ''}`}
                                                    onClick={() => handleMaintenanceSubTabClick(subTab)}
                                                >
                                                    <span className="sub-nav-label">{subTab.charAt(0).toUpperCase() + subTab.slice(1)}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* Utility Sub-tabs */}
                                {tab.name === 'utility' && expandedStates.utility && (
                                    <ul className="sub-nav-list">
                                        {['paid', 'unpaid'].map((subTab) => (
                                            <li key={subTab} className="sub-nav-item">
                                                <button
                                                    className={`sub-nav-button ${isUtilitySubTabSelected(subTab) ? 'selected' : ''}`}
                                                    onClick={() => handleUtilitySubTabClick(subTab)}
                                                >
                                                    <span className="sub-nav-label">{subTab.charAt(0).toUpperCase() + subTab.slice(1)}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* Tickets Sub-tabs */}
                                {tab.name === 'tickets' && expandedStates.tickets && (
                                    <ul className="sub-nav-list">
                                        {['requests', 'closed', hasShowCategory && 'categories']
                                            .filter(Boolean) // Remove false values when hasShowCategory is false
                                            .map((subTab) => (
                                                <li key={subTab} className="sub-nav-item">
                                                    <button
                                                        className={`sub-nav-button ${isTicketsSubTabSelected(subTab) ? 'selected' : ''}`}
                                                        onClick={() => handleTicketsSubTabClick(subTab)}
                                                    >
                                                        <span className="sub-nav-label">{subTab.charAt(0).toUpperCase() + subTab.slice(1)}</span>
                                                    </button>
                                                </li>
                                            ))}
                                    </ul>
                                )}

                                {/* logs Sub-tabs */}
                                {tab.name === 'logs' && expandedStates.logs && (
                                    <ul className="sub-nav-list">
                                        {[
                                            hasShowAdminLogs && 'admin',
                                            hasShowUserLogs && 'user'
                                        ]
                                            .filter(Boolean) // Remove false values when permissions are false
                                            .map((subTab) => (
                                                <li key={subTab} className="sub-nav-item">
                                                    <button
                                                        className={`sub-nav-button ${isLogsSubTabSelected(subTab) ? 'selected' : ''}`}
                                                        onClick={() => handleLogsSubTabClick(subTab)}
                                                    >
                                                        <span className="sub-nav-label">{subTab.charAt(0).toUpperCase() + subTab.slice(1)}</span>
                                                    </button>
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                </ul>
            </nav>
        </div>
    );
});

export default Sidebar;